/* ImageDetailsRenderer.js */

"use strict";
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const _ = require("lodash");
const NumberInputControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputNumber;
const InputRadioControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .InputRadio;
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;
const OsPlanCatalogImagesComponent = require("./osplancatalogimages/OsPlanCatalogImagesComponent");
const OsPlanServerImagesComponent = require("./osplanserverimages/OsPlanServerImagesComponent");
class ImageDetailsRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("image-details-view-gl");
        this._rootElem.component = this._model;
        this._targetDevice = document.createElement("div");
        this._targetDevice.classList.add("target-device");

        this._imageProtocolContainer = document.createElement("div");
        this._imageProtocolContainer.classList.add("image-protocol-container");

        this._imageSourceContainer = document.createElement("div");
        this._imageSourceContainer.classList.add("image-source-container");

        this._imageSourceLabel = document.createElement("label");
        this._imageSourceLabel.innerHTML = "Image Source ";

        this._imagesSection = undefined;
        this._catalogImg = undefined;

        this._imageSource = new InputRadioControl({
            horizontal: true,
            options: [
                {
                    label: "Server",
                    value: "server",
                },
                {
                    label: "Catalog",
                    value: "catalog",
                },
            ],
            value: "catalog",
        });
        this._currentSource = "catalog";
        this._transferProtocolContainer = document.createElement("div");
        this._transferProtocolContainer.classList.add("transfer-protocol-container");
        this._transferProtocolLabel = document.createElement("label");
        this._transferProtocolLabel.innerHTML = "Transfer Protocol ";
        this._transferProtocol = new ComboBoxControl({
            options: this.model.getAssociationProtocols(),
            value: this.model.getSelectedAssociation() || "SCP",
        });

        this._windowSizeContainerParent = document.createElement("div");
        this._windowSizeLabel = document.createElement("label");
        this._windowSizeLabel.innerHTML = "Window size";
        this._windowSize = new NumberInputControl({
            min: 8192,
            max: 131072,
            step: 8,
            value: this._model.getSelectedWindowSize() || 8192,
        });

        this._logger = this.model.logger;
        this._imagesSectionContainer = document.createElement("div");
        this._formsComponent = new FormsComponent();
        this._registerListeners();
    }

    async _checkIfCiscoIOS() {
        if (_.get(this._model._executionEntity, "pristineProperties", false)) {
            return (
                _.get(this._model._executionEntity, "pristineProperties.detectedVendor", "") ===
                    "Cisco" &&
                _.get(this._model._executionEntity, "pristineProperties.detectedOs", "")
                    .toLowerCase()
                    .indexOf("ios") > -1
            );
        } else {
            const devices = await this._model._osPlan.loadRelatedDevices(
                {detectedSku: _.get(this._model._executionEntity, "groupingValue", "")},
                {id: true, detectedVendor: true, detectedOs: true}
            );
            let isCiscoIOS = true;
            devices.forEach((device) => {
                if (
                    _.get(device, "pristineProperties.detectedVendor", "") !== "Cisco" ||
                    _.get(device, "pristineProperties.detectedOs", "")
                        .toLowerCase()
                        .indexOf("ios") === -1
                ) {
                    isCiscoIOS = false;
                }
            });
            return isCiscoIOS;
        }
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        this._unregisterListeners();
        this._removeViewActions();

        let promArr = [];
        if (this._formsComponent) {
            promArr.push(this._formsComponent.shutdown());
        }
        if (this._imagesSection) {
            promArr.push(this._imagesSection.shutdown());
        }
        if (this._catalogImg) {
            promArr.push(this._catalogImg.shutdown());
        }

        return Promise.all(promArr).then(
            () => {
                this._formsComponent = undefined;
                this._imagesSection = undefined;
                this._catalogImg = undefined;
                this._targetDevice = undefined;
                this._imageSource = undefined;
                this._imageSourceLabel = undefined;
                this._imageSourceContainer.remove();
                this._imageSourceContainer = undefined;
                this._imageProtocolContainer.remove();
                this._imageProtocolContainer = undefined;
                this._transferProtocolContainer.remove();
                this._transferProtocolContainer = undefined;
                this._transferProtocol = undefined;
                this._windowSize = undefined;
                this._renderingForm = undefined;
                this._currentSource = undefined;
                this._logger = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ImageDetailsRenderer.shutdown() error.");
            }
        );
    }

    _registerListeners() {
        this._imageSource.on(
            "Value",
            (this._toggleImageSourceAction = this._toggleImageSourceAction.bind(this))
        );
        this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));
        this._transferProtocol.on(
            "Value",
            (this._onUserChangeTransferProtocol = this._onUserChangeTransferProtocol.bind(this))
        );
        this._windowSize.on(
            "Value",
            (this._onUserChangeTransferProtocol = this._onUserChangeTransferProtocol.bind(this))
        );
    }

    _unregisterListeners() {
        this._imageSource.removeAllListeners("Value");
        this._model.removeListener("Dirty", this._onDirty);
    }

    _targetImageDevice() {
        let targetValue = "";
        if (_.get(this._model._gameEditor, "_activeView._groupBy", false) === "None") {
            targetValue = ` ${_.get(
                this._model._executionEntity,
                "pristineProperties.name",
                ""
            )} - ${_.get(this._model._executionEntity, "pristineProperties.detectedSku", "")}`;
        } else {
            targetValue = `${_.get(this._model._executionEntity, "groupingValue", "")}`;
        }
        this._targetDevice.innerText = `Target: ${targetValue}`;
        this._rootElem.appendChild(this._targetDevice);
    }

    _renderImages(imageSource) {
        this._imagesSectionContainer.innerHTML = "";
        if (imageSource === "server") {
            this._currentSource = "server";
            if (this._catalogImg) {
                this._catalogImg.shutdown();
            }
            return Promise.resolve().then(() => {
                this._imagesSection = new OsPlanServerImagesComponent(this._model);
                this._imagesSection.renderer.render().then(() => {
                    this._imagesSectionContainer.appendChild(
                        this._imagesSection.renderer.rootElement
                    );
                    this._rootElem.appendChild(this._imagesSectionContainer);
                    this._imagesSection.renderer._treeComponent.renderer.render();
                });
            });
        } else {
            this._currentSource = "catalog";
            if (this._imagesSection) {
                this._imagesSection.shutdown();
            }
            return Promise.resolve().then(() => {
                this._catalogImg = new OsPlanCatalogImagesComponent(this._model);
                this._catalogImg.renderer.render().then(() => {
                    this._imagesSectionContainer.appendChild(this._catalogImg.renderer.rootElement);
                    this._rootElem.appendChild(this._imagesSectionContainer);
                });
            });
        }
    }

    _registerViewAction() {
        return new Promise((resolve) => {
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Save",
                name: "SavePlanImageAssociations",
                handler: (this._onPlanImageAssociationsSave =
                    this._onPlanImageAssociationsSave.bind(this)),
            });
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Cancel",
                name: "CancelPlanImageAssociationsDevice",
                handler: (this._onPlanImageAssociationsCancel =
                    this._onPlanImageAssociationsCancel.bind(this)),
            });
            resolve();
        });
    }

    _hideViewAction() {
        this.model.gameEditor.hideViewAction("SavePlanImageAssociations");
        this.model.gameEditor.hideViewAction("CancelPlanImageAssociationsDevice");
    }
    _disableViewActions() {
        this.model.gameEditor.disableViewAction("SavePlanImageAssociations");
        this.model.gameEditor.disableViewAction("CancelPlanImageAssociationsDevice");
    }

    _enableViewActions() {
        this.model.gameEditor.enableViewAction("SavePlanImageAssociations");
        this.model.gameEditor.enableViewAction("CancelPlanImageAssociationsDevice");
    }
    _removeViewActions() {
        this.model.gameEditor.removeViewAction("SavePlanImageAssociations");
        this.model.gameEditor.removeViewAction("CancelPlanImageAssociationsDevice");
    }
    _toggleAction(isDirty) {
        if (isDirty) {
            this._enableViewActions();
        } else {
            this._disableViewActions();
        }
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this.model
            .whenModelIsReady()
            .then(() => this._checkIfCiscoIOS())
            .then((isCiscoIOS) => {
                this._isCiscoIOS = isCiscoIOS;
                this._targetImageDevice();
                this._imageSourceContainer.appendChild(this._imageSourceLabel);
                this._imageSourceContainer.appendChild(this._imageSource.renderer.rootElement);
                this._transferProtocolContainer.appendChild(this._transferProtocolLabel);
                this._transferProtocolContainer.appendChild(
                    this._transferProtocol.renderer.rootElement
                );

                this._windowSizeContainerParent.classList.add("window-size-container-parent");
                const windowSizeContainer = document.createElement("div");
                windowSizeContainer.classList.add("window-size-container");
                this._windowSizeContainerParent.appendChild(windowSizeContainer);
                windowSizeContainer.appendChild(this._windowSizeLabel);
                windowSizeContainer.appendChild(this._windowSize.renderer.rootElement);

                if (this._transferProtocol.getValue() !== "SCP" || !this._isCiscoIOS) {
                    this._windowSizeContainerParent.classList.add("disabled");
                } else {
                    this._windowSizeContainerParent.classList.remove("disabled");
                }

                this._imageProtocolContainer.appendChild(this._imageSourceContainer);
                this._imageProtocolContainer.appendChild(this._transferProtocolContainer);
                this._imageProtocolContainer.appendChild(this._windowSizeContainerParent);
                this._rootElem.appendChild(this._imageProtocolContainer);

                this._renderImages(this._imageSource.getValue());
                _.delay(_.bind(this._setRadioButtons, this), 300);
                this._registerViewAction();
                return;
            })
            .then(() => {
                this._toggleAction(this._model._osPlan.isDirty());
            })
            .catch((err) => {
                this._logger.warn({error: err}, "ImageDetailsRenderer.render() - Error");
            });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    _setRadioButtons() {
        if (!_.get(this._model._osUpgradePrefEntity, "pristineProperties.enableCatalogs", false)) {
            this._disableCatalogOption();
        } else {
            if (this._model._authorizedActions.indexOf("osmCatalogsRequired") >= 0) {
                this._disableServerOption();
            } else if (
                _.get(this._model._osUpgradePrefEntity, "pristineProperties.catalogUsage", "") ===
                "SUBORDINATES"
            ) {
                this._model
                    .isUserAuthorizedForCreate(this._model._activeOrgId)
                    .then((isUserAuthorized) => {
                        if (!isUserAuthorized) {
                            this._disableServerOption();
                        }
                    });
            }
        }
    }

    _disableCatalogOption() {
        this._imageSource.setSelectedOption("server"); // Set server as default option
        this._imageSource.getRenderer()._buttons[1].setAttribute("disabled", true);
    }

    _disableServerOption() {
        this._imageSource.getRenderer()._buttons[0].setAttribute("disabled", true);
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onDirty(dirtyState) {
        this.model._isDirty = dirtyState;
        this._toggleAction(dirtyState);
    }

    _onPlanImageAssociationsSave() {
        this._disableViewActions();
        return this._model
            ._saveImageAssociations()
            .then(() => {
                this.model.isDirty = false;
                this._disableViewActions();
            })
            .catch((/*err*/) => {
                this._enableViewActions();
            });
    }

    _onPlanImageAssociationsCancel() {
        this.model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
            },
            (this._onPlanImageAssociationsCancelAction =
                this._onPlanImageAssociationsCancelAction.bind(this))
        );
    }

    _onPlanImageAssociationsCancelAction() {
        this.model._osPlan.clearPendingRelationships(true);
        this.model._osPlan.clearPendingPatches();
        this.model.isDirty(false);
        this.render();
    }

    _onUserChangeTransferProtocol() {
        let protocol = this._transferProtocol.getValue();
        let windowSize = this._windowSize.getValue();
        let key = "";

        if (protocol !== "SCP" || !this._isCiscoIOS) {
            this._windowSizeContainerParent.classList.add("disabled");
            windowSize = undefined;
        } else {
            this._windowSizeContainerParent.classList.remove("disabled");
        }

        if (this._imageSource.getValue() === "server") {
            key = this._imagesSection._key;
        } else {
            key = this._catalogImg._key;
        }
        Object.values(this.model._osPlan.getPristineProperties().imageAssociations).map(
            (association) => {
                association.protocol = protocol;
                association.copyBlockSize = windowSize;
            }
        );
        this.model._osPlan.changeAssociationProtocol(key, protocol, windowSize);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _toggleImageSourceAction() {
        if (this._currentSource !== this._imageSource.getValue()) {
            this._renderImages(this._imageSource.getValue()).then(() => {
                this._toggleAction(this.model._osPlan.isDirty());
            });
        }
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = ImageDetailsRenderer;
